import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { AddLetterForm } from "./add-letter-form";
import { AddLetter, GetLetterById, EditLetter } from "../../services/letter";
import { convertToJalaliNoTime } from "../../utils/convertToJalali";

export default function AddLetterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location.state?.mode === "edit";
  const letterId = location.state?.letterId;
  const currentUserId = useSelector((state) => state.user.iduser);

  const [formData, setFormData] = useState({
    dateletter: new Date().toISOString(), // Default to current date in ISO format
    numletter: "",
    isattach: false,
    attachmentNum: "",
    sender: "",
    receiver: "",
    title: "",
    textletter: "",
    fullname: "",
    postName: "",
    isroonevesht: false,
    roonevesht: "",
    isdate: true,
    isnumletter: true,
    lang: "fa",
    file: null,
    idemza: "",
    idsarbarg: "",
    idletter: 0,
    idletterstr: "",
    xdate: 0,
    ydate: 0,
    xatach: 0,
    yatach: 0,
    xnumletter: 0,
    ynumletter: 0,
  });

  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [emzaDrawerOpen, setEmzaDrawerOpen] = useState(false);
  const [letterheadDrawerOpen, setLetterheadDrawerOpen] = useState(false);

  // Fetch letter data when in edit mode
  useEffect(() => {
    const fetchLetterData = async () => {
      if (isEditMode && letterId) {
        try {
          setIsLoadingData(true);
          const response = await GetLetterById(letterId);
          const letter = response.data;

          // Map API response to form data
          setFormData({
            dateletter: letter.dateletter,
            numletter: letter.numletter || "",
            isattach: letter.isattach || letter.isAttachment || false,
            attachmentNum: letter.attachmentNum || "",
            sender: letter.sender || "",
            receiver: letter.receiver || "",
            title: letter.title || "",
            textletter: letter.textletter || "",
            fullname: letter.fullname || "",
            postName: letter.postName || "",
            isroonevesht: letter.isroonevesht || false,
            roonevesht: letter.roonevesht || "",
            isdate: letter.isdate !== undefined ? letter.isdate : true,
            isnumletter:
              letter.isnumletter !== undefined ? letter.isnumletter : true,
            lang: "fa",
            file: null,
            idemza: letter.idemzastr || "",
            idsarbarg: letter.idsarbargstr || "",
            idletter: letter.idletter || 0,
            idletterstr: letter.idletterstr || letterId || "",
          });
        } catch (error) {
          console.error("Error fetching letter data:", error);
          Swal.fire({
            title: "خطا",
            text: "مشکلی در دریافت اطلاعات نامه رخ داد",
            confirmButtonText: "باشه",
          }).then(() => {
            navigate("/letter");
          });
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchLetterData();
  }, [isEditMode, letterId, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (file) => {
    setFormData((prev) => ({ ...prev, file }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      // Validate required fields
      if (
        !formData.title ||
        !formData.textletter ||
        !formData.sender ||
        !formData.receiver
      ) {
        setMessage({
          type: "error",
          text: "لطفاً تمام فیلدهای ضروری را پر کنید",
        });
        setLoading(false);
        return;
      }

      // Helper to get single value (handle arrays from FormData)
      const getValue = (value) => {
        if (Array.isArray(value)) {
          return value[0] || "";
        }
        return value || "";
      };

      // Helper to get single string value
      const getStringValue = (value) => {
        const val = getValue(value);
        return typeof val === "string" ? val : String(val || "");
      };

      // Helper to get number value
      const getNumberValue = (value) => {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      };

      // Helper to get boolean value
      const getBooleanValue = (value) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
          return value === "true" || value === "1";
        }
        return Boolean(value);
      };

      // Create JSON payload according to API schema
      const submitData = {
        idUser: getNumberValue(currentUserId),
        idemza: 0, // Numeric ID, default to 0
        idemzastr: getStringValue(formData.idemza),
        idsarbarg: 0, // Numeric ID, default to 0
        idsarbargstr: getStringValue(formData.idsarbarg),
        title: getStringValue(formData.title),
        sender: getStringValue(formData.sender),
        receiver: getStringValue(formData.receiver),
        textletter: getStringValue(formData.textletter),
        isroonevesht: getBooleanValue(formData.isroonevesht),
        roonevesht: getStringValue(formData.roonevesht),
        numletter: getStringValue(formData.numletter),
        dateletter: getStringValue(formData.dateletter) || new Date().toISOString(),
        lang: getStringValue(formData.lang) || "fa",
        isAttachment: getBooleanValue(formData.isattach),
        fullname: getStringValue(formData.fullname),
        postName: getStringValue(formData.postName),
        isdate: getBooleanValue(formData.isdate),
        isattach: getBooleanValue(formData.isattach),
        isnumletter: getBooleanValue(formData.isnumletter),
        xdate: getNumberValue(formData.xdate),
        ydate: getNumberValue(formData.ydate),
        xatach: getNumberValue(formData.xatach),
        yatach: getNumberValue(formData.yatach),
        xnumletter: getNumberValue(formData.xnumletter),
        ynumletter: getNumberValue(formData.ynumletter),
      };

      if (isEditMode && letterId) {
        // Add letter ID fields for edit mode
        submitData.idletter = getNumberValue(formData.idletter);
        submitData.idletterstr = getStringValue(formData.idletterstr) || letterId;
        
        // Edit existing letter
        await EditLetter(letterId, submitData);
        setMessage({ type: "success", text: "نامه با موفقیت ویرایش شد" });
        // Navigate back to letter list after successful edit
        setTimeout(() => {
          navigate("/letter");
        }, 1500);
      } else {
        // Add new letter
        await AddLetter(submitData);
        setMessage({ type: "success", text: "نامه با موفقیت ثبت شد" });
      }
    } catch (error) {
      console.error("Error submitting letter:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          (isEditMode ? "خطا در ویرایش نامه" : "خطا در ثبت نامه"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: "center",
        p: 1,
        pb: 3,
      }}
    >
      <Box sx={{ width: "100%" }}>
        <AddLetterForm
          formData={formData}
          onChange={handleInputChange}
          onFileChange={handleFileChange}
          onOpenEmzaDrawer={() => setEmzaDrawerOpen(true)}
          onOpenLetterheadDrawer={() => setLetterheadDrawerOpen(true)}
          onSelectEmza={(id) => {
            if (id !== null && id !== undefined) {
              handleInputChange("idemza", id);
            }
            setEmzaDrawerOpen(false);
          }}
          onSelectLetterhead={(id) => {
            if (id !== null && id !== undefined) {
              handleInputChange("idsarbarg", id);
            }
            setLetterheadDrawerOpen(false);
          }}
          onCloseEmzaDrawer={() => setEmzaDrawerOpen(false)}
          onCloseLetterheadDrawer={() => setLetterheadDrawerOpen(false)}
          emzaDrawerOpen={emzaDrawerOpen}
          letterheadDrawerOpen={letterheadDrawerOpen}
          currentUserId={currentUserId}
        />
      </Box>

      {message.text && (
        <Alert
          severity={message.type}
          sx={{ width: "100%", maxWidth: "210mm" }}
        >
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          p: 1,
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            width: "100%",
            maxWidth: "210mm",
            borderRadius: "12px",
            padding: "10px",
            border: "1px solid white",
            textTransform: "none",
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <Typography variant="h5" sx={{ marginRight: "10px" }}>
              {isEditMode ? "ویرایش نامه" : "افزودن نامه"}
            </Typography>
          )}
        </Button>
      </Box>
    </Box>
  );
}
